import { Alert, Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "i18n-fe";
import { useStationMonitor } from "../hooks/useStationMonitor";
import { formatElapsedLabel } from "../util/scribePresence";

// #2827 (scribe-resilience workstream, G12 part B) — "Placement, decided
// (Jaybill, 2026-09-20): its own page, plus a one-line summary strip on the
// existing show dashboard showing worst-case counts and oldest-unshipped,
// linking through. One place to glance, one place to work." (docs/score/
// scribe-resilience.md). Deliberately reuses useStationMonitor — the SAME
// rows/summary the full StationMonitor page renders (util/stationMonitor.js's
// summarizeStationMonitorRows) — so this strip and the monitor page can never
// disagree about a count. #3090: it renders inside the dashboard's Scribe
// section (ScribePresenceSection), not above the stats cards, and is hidden
// when no scribe is connected AND the summary is clean (#3097: non-clean work
// keeps the warning visible even with nobody connected); everything per-station lives on the monitor
// page.
//
// #2827 judge fix r1 (FE item 1): "clean" must be a hard AND across every
// worst-case number — needsAttention, queued, never-uploaded, and the very
// presence of an oldest-unshipped timestamp — not just needsAttention/
// neverUploaded. A show with a pile of merely-queued sheets (nothing flagged
// yet) is not clean, and this strip is exactly the "predicts a bad
// afternoon" glance the card exists for.
export default function StationMonitorStrip({ showId }) {
  const { t } = useTranslation("scoreadmin");
  const { rows, summary, loading, error } = useStationMonitor(showId);

  if (!showId || loading || error || !summary) return null;
  const { needsAttentionTotal, queuedTotal, neverUploadedCount, oldestUnshippedAt } = summary;
  const clean =
    needsAttentionTotal === 0 && queuedTotal === 0 && neverUploadedCount === 0 && !oldestUnshippedAt;

  // #3090 (bugs board #261) -- with no scribe connected and nothing pending, a
  // green "all stations reporting clean" over an empty tent reads as a false
  // all-clear, so the strip stays hidden. #3097 -- but if ANY queued /
  // needs-attention / never-uploaded / oldest-unshipped work exists, the
  // station that holds it has usually just dropped out of presence (60s TTL:
  // tablet died, closed, or lost network), and that is exactly the stranded
  // score case this strip exists for (scribe-resilience P2 "no stranding"),
  // so the warning stays visible. `summary` covers every row, connected or not.
  const anyConnected = rows.some((r) => r.connected);
  if (!anyConnected && clean) return null;

  return (
    <Alert
      severity={clean ? "success" : "warning"}
      variant="outlined"
      data-testid="station-monitor-strip"
      sx={{ mb: 2 }}
    >
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        {clean ? (
          <Typography variant="body2" data-testid="station-monitor-strip-ok">
            {t("stationMonitor.strip.ok")}
          </Typography>
        ) : (
          <>
            {needsAttentionTotal > 0 && (
              <Typography variant="body2" data-testid="station-monitor-strip-needs-attention">
                {t("stationMonitor.strip.needsAttention", { count: needsAttentionTotal })}
              </Typography>
            )}
            {queuedTotal > 0 && (
              <Typography variant="body2" data-testid="station-monitor-strip-queued">
                {t("stationMonitor.strip.queued", { count: queuedTotal })}
              </Typography>
            )}
            {neverUploadedCount > 0 && (
              <Typography variant="body2" data-testid="station-monitor-strip-never-uploaded">
                {t("stationMonitor.strip.neverUploaded", { count: neverUploadedCount })}
              </Typography>
            )}
            {oldestUnshippedAt && (
              <Typography variant="body2" data-testid="station-monitor-strip-oldest">
                {t("stationMonitor.strip.oldestUnshipped", {
                  time: formatElapsedLabel(Math.max(0, Date.now() - new Date(oldestUnshippedAt).getTime())),
                })}
              </Typography>
            )}
          </>
        )}
        <Box sx={{ ml: "auto" }}>
          <MuiLink component={RouterLink} to="/station-monitor" data-testid="station-monitor-strip-link">
            {t("stationMonitor.strip.link")}
          </MuiLink>
        </Box>
      </Stack>
    </Alert>
  );
}
